import React from "react";

import { SkeletonBox } from "~/components/skeleton/box";
import { SkeletonList } from "~/components/skeleton/list";
import { SkeletonText } from "~/components/skeleton/text";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import { prop } from "~/tools/prop";
import { useHandler } from "~/tools/use-handler";

import { type SystemNotification, SystemNotificationSeverity } from "../../common/openapi";
import { useUserProfile } from "../../common/use-user-profile";
import { Button } from "../react/buttons/Button";
import { Markdown } from "../react/markdown/Markdown";
import { DONT_AUTO_DISMISS, type NotificationArgs, notification as notifier } from "../react/notification/Notification";
import { FormatViewTime } from "../react/viewtime/FormatViewTime";

import { SystemNotificationSeverityIcon } from "./_system-notification-severity-icon";

interface SystemNotificationProps {
	notifications: undefined | ReadonlyArray<SystemNotification>;
	className?: string;
}

export const SystemNotifications: React.FC<SystemNotificationProps> = ({ notifications, ...props }) => {
	const [systemNotificationsRead, updateUserProfile] = useUserProfile({
		select: prop("systemNotificationsRead"),
	});

	return (
		<SkeletonList
			className="divide-y divide-grey-300"
			count={2}
			each={notifications}
			el="div"
			empty={<EmptySystemNotifications />}
			{...props}
		>
			{(notification) => (
				<NotificationItem
					isRead={notification == null || (systemNotificationsRead?.has(notification.notificationId) ?? true)}
					key={notification?.notificationId}
					notification={notification}
					onMarkAsRead={() => {
						if (notification) {
							updateUserProfile((userProfile) => ({
								...userProfile,
								systemNotificationsRead: userProfile.systemNotificationsRead.add(notification.notificationId),
							}));
						}
					}}
					onToggleRead={() => {
						if (notification) {
							updateUserProfile((userProfile) => ({
								...userProfile,
								systemNotificationsRead: userProfile.systemNotificationsRead.toggle(notification.notificationId),
							}));
						}
					}}
				/>
			)}
		</SkeletonList>
	);
};

interface NotificationItemProps {
	notification: undefined | SystemNotification;
	isRead: boolean;
	onMarkAsRead: VoidFunction;
	onToggleRead: VoidFunction;
}

const NotificationItem: React.FC<NotificationItemProps> = ({ notification, isRead, onMarkAsRead, onToggleRead }) => {
	const stableOnMarkAsRead = useHandler(onMarkAsRead);

	React.useEffect(() => {
		if (!notification?.toast) {
			return;
		}

		if (isRead === true) {
			notifier.close(notification.notificationId);
			return;
		}

		const notificationConfig = {
			key: notification.notificationId,
			duration: DONT_AUTO_DISMISS,
			icon: <SystemNotificationSeverityIcon severity={notification.severity} />,
			message: notification.title,
			description: <Markdown text={notification.content} />,
			priority: notification.notificationTimeEpochMs,
			onClose: stableOnMarkAsRead,
		} satisfies NotificationArgs;

		switch (notification.severity) {
			case SystemNotificationSeverity.Problem:
				notifier.error(notificationConfig);
				break;
			case SystemNotificationSeverity.Warning:
				notifier.warning(notificationConfig);
				break;
			case SystemNotificationSeverity.Info:
				notifier.info(notificationConfig);
				break;
			case SystemNotificationSeverity.Ok:
				notifier.success(notificationConfig);
				break;
		}
	}, [notification, stableOnMarkAsRead, isRead]);

	return (
		<div className={cx("flex gap-3 p-4", !isRead && "bg-core-fill-brand-tertiary-default")}>
			<div className="flex h-6 items-center text-xl">
				<NotificationTypeIcon severity={notification?.severity} />
			</div>

			<div className="min-w-0 flex-1">
				<div className="flex select-none items-center justify-between gap-4">
					<SkeletonText className="skeleton:w-32 min-w-0 text-grey-900" el="div" randomize when={notification?.title}>
						{(title) => <Truncated>{title}</Truncated>}
					</SkeletonText>
					<Button
						className="-mr-2"
						disabled={!notification}
						icon={
							<SkeletonBox
								className={cx(
									"h-2 w-2 rounded-full",
									isRead ? "border border-core-stroke-brand-default" : "bg-core-fill-brand-primary-default",
								)}
								el="span"
								when={notification}
							/>
						}
						onClick={onToggleRead}
						shape="circle"
						size="small"
						title={isRead ? "Mark as unread" : "Mark as read"}
						type="link"
					/>
				</div>

				<div className="space-y-0.75">
					<SkeletonText
						className="skeleton:w-20 text-grey-600 text-xs leading-5"
						el="div"
						randomize
						when={notification?.notificationTimeEpochMs}
					>
						{(notificationTimeEpochMs) => <FormatViewTime time={notificationTimeEpochMs} />}
					</SkeletonText>

					<SkeletonText el="div" lines={3} randomize when={notification?.content}>
						{(content) => <Markdown text={content} />}
					</SkeletonText>
				</div>
			</div>
		</div>
	);
};

const NotificationTypeIcon: React.FC<{
	severity: undefined | SystemNotificationSeverity;
}> = ({ severity }) => {
	if (severity == null) {
		return <SkeletonBox className="h-5 w-5 rounded-full" el="svg" />;
	}

	return <SystemNotificationSeverityIcon severity={severity} />;
};

const EmptySystemNotifications: React.FC = () => (
	<div className="border-grey-300 border-t px-4 py-10 text-center text-grey-900 text-sm">
		<span className="text-[53px] leading-none" role="img">
			🙌
		</span>

		<h5 className="mt-4 mb-0 font-bold">You’re all caught up!</h5>
		<p className="m-0">Carry on...</p>
	</div>
);
